import { Button } from './Button';
import { cn } from '@/utils/cn';

export interface PaginationProps {
  /** Current 1-indexed page */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Called with the new page number when the user navigates */
  onPageChange: (page: number) => void;
  /** Additional classes appended to the base styles. Note: not guaranteed to override conflicting base utilities (cn is a concat, not tailwind-merge) — prefer additive classes. */
  className?: string;
}

type PageItem = number | 'ellipsis-start' | 'ellipsis-end';

/**
 * Build a truncated list of page numbers to render:
 * always show first + last page, current page +/-1, collapse the rest
 * into an ellipsis. Shows all pages if there are 7 or fewer.
 */
const getPageItems = (page: number, totalPages: number): PageItem[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const items: PageItem[] = [1];

  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);

  if (start > 2) {
    items.push('ellipsis-start');
  }

  for (let i = start; i <= end; i++) {
    items.push(i);
  }

  if (end < totalPages - 1) {
    items.push('ellipsis-end');
  }

  items.push(totalPages);

  return items;
};

/**
 * Pagination - Prev / numbered pages (with ellipsis truncation) / Next.
 * Drop-in replacement for the hand-rolled Prev-Next-only button pairs in
 * Users.tsx / Teams.tsx / Agencies.tsx (`<div className="flex gap-2">...</div>`
 * inside their `flex items-center justify-between` pagination footer).
 *
 * @example
 * <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
 *   <div className="text-sm text-gray-500">Showing 1 to 20 of 143</div>
 *   <Pagination page={data.meta.page} totalPages={data.meta.totalPages} onPageChange={handlePageChange} />
 * </div>
 */
export const Pagination = ({ page, totalPages, onPageChange, className = '' }: PaginationProps) => {
  if (totalPages <= 1) return null;

  // Defensively clamp a stale `page` (e.g. > totalPages after deleting the
  // last row on the last page) so Next's disabled state and the highlighted
  // page stay correct.
  const safePage = Math.min(Math.max(page, 1), totalPages);

  const items = getPageItems(safePage, totalPages);

  return (
    <nav aria-label="Pagination" className={cn('flex items-center gap-1', className)}>
      <Button
        variant="secondary"
        size="sm"
        aria-label="Previous page"
        disabled={safePage === 1}
        onClick={() => onPageChange(safePage - 1)}
      >
        Previous
      </Button>

      <div className="flex items-center gap-1 mx-1">
        {items.map((item) =>
          item === 'ellipsis-start' || item === 'ellipsis-end' ? (
            <span key={item} className="px-2 text-sm text-gray-400" aria-hidden="true">
              &hellip;
            </span>
          ) : (
            <Button
              key={item}
              variant={item === safePage ? 'secondary' : 'outline'}
              size="sm"
              aria-label={`Page ${item}`}
              aria-current={item === safePage ? 'page' : undefined}
              onClick={() => onPageChange(item)}
            >
              {item}
            </Button>
          )
        )}
      </div>

      <Button
        variant="secondary"
        size="sm"
        aria-label="Next page"
        disabled={safePage === totalPages}
        onClick={() => onPageChange(safePage + 1)}
      >
        Next
      </Button>
    </nav>
  );
};

export default Pagination;
